<template>
  <page-wrapper :title="title">
    <!-- Loading Section -->
    <LoadingOverlay :active="loading" />

    <div>
      <!-- Search Bar -->
      <virtual-sport-search-bar
        :search-param="searchParam"
        @search="handleSearch"
        @tabChange="handleTabChange"
        @searchParam="handleUpdateSearchParam"
      />
      <!-- Data List -->
      <div class="data-list-container">
        <div class="results-header">
          <div>
            <span>{{ total }} รายการ</span>
          </div>
          <div>
            <p>
              แสดงผล {{ getCurrentPageRange() }} จากทั้งหมด {{ total }} รายการ
            </p>
          </div>
        </div>

        <div v-if="!items || items.length === 0" class="empty-state">
          <h3 class="empty-title">ไม่พบข้อมูล</h3>
        </div>

        <div v-else class="items-list">
          <virtual-sport-list-item
            v-for="item in items"
            :key="item.id"
            :item="item"
            @onClick="handleApprovalButton(item)"
          />
        </div>

        <!-- Pagination -->
        <div class="pagination-container">
          <el-pagination
            :total="total"
            :page-size="5"
            :current-page="page"
            background
            layout="prev, pager, next"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
// Components
import PageWrapper from "@/lib/components/PageWrapper"
import LoadingOverlay from "@/lib/components/LoadingOverlay"
import { backendAdminClient } from "@/dependencies"
import VirtualSportSearchBar from "../components/VirtualSportSearchBar"
import VirtualSportListItem from "../components/VirtualSportListItem"

// Enums
import { virtualSportApprovalStatus } from "../config/enums"

// Client

export default {
  components: {
    PageWrapper,
    LoadingOverlay,
    VirtualSportSearchBar,
    VirtualSportListItem,
  },
  data() {
    const searchParam = {
      projectId: this.$route.query.projectId || null,
      addressNo: null,
      firstName: null,
      lastName: null,
      startDate: null,
      endDate: null,
      status: this.$route.query.status || virtualSportApprovalStatus.PENDING,
    }

    return {
      title: "Virtual Sport",
      loading: false,
      total: 0,
      page: 1,
      searchParam,
      statusTab: virtualSportApprovalStatus.PENDING,
      items: [],
    }
  },
  watch: {
    "$route.query.projectId": function routeQueryProjectIdWatcher(
      newProjectId,
    ) {
      if (newProjectId) {
        this.searchParam.projectId = newProjectId
        this.resetPageAndSearch()
      }
    },
  },
  created() {
    this.setUpData()
  },
  methods: {
    async setUpData() {
      if (!this.searchParam.projectId) {
        this.items = []
        this.total = 0
        return
      }

      const result = await this.fetchData()
      this.items = this.mapRecordsToItems(result.records)
      this.total = result.pagination?.total_records || 0
    },

    mapRecordsToItems(records) {
      if (!records || !Array.isArray(records)) {
        return []
      }

      return records.map((record) => ({
        id: record.id,
        evidenceImageUrl: record.evidence_image_url,
        projectCode: record.project_code,
        addressNo: record.address_no,
        customerName: record.customer_name,
        status: record.status,
        createdAt: record.created_at,
        points: record.points,
        calories: record.calories,
        workoutDurationSeconds: record.workout_duration_seconds,
        rejectionReason: record.rejection_reason,
      }))
    },

    async fetchData() {
      this.loading = true

      try {
        const apiParams = {
          page: this.page,
          limit: 5,
          status: this.searchParam.status,
          project_id: this.searchParam.projectId,
          address_no: this.searchParam.addressNo,
          first_name: this.searchParam.firstName,
          last_name: this.searchParam.lastName,
          start_date: this.searchParam.startDate,
          end_date: this.searchParam.endDate,
        }

        const { data } = await backendAdminClient.virtualSport.list(apiParams)

        return data.result
      } catch (error) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")

        return {
          pagination: {
            total_records: 0,
            page: this.page,
            limit: 5,
          },
          records: [],
        }
      } finally {
        this.loading = false
      }
    },

    handleTabChange(tabKey) {
      this.statusTab = tabKey
      this.searchParam = { ...this.searchParam, status: tabKey }
      this.updateUrlQuery()
      this.resetPageAndSearch()
    },

    handleUpdateSearchParam(updatedSearchParam) {
      this.searchParam = updatedSearchParam
      this.updateUrlQuery()
    },

    async handleSearch() {
      this.updateUrlQuery()
      this.resetPageAndSearch()
    },

    handleApprovalButton(item) {
      this.$router.push({
        name: "VirtualSportApprovalPage",
        params: { id: item.id },
      })
    },

    handleCurrentChange(page) {
      this.page = page
      this.setUpData()
    },

    resetPageAndSearch() {
      this.page = 1
      this.setUpData()
    },

    updateUrlQuery() {
      const query = { ...this.$route.query }

      if (this.searchParam.projectId) {
        query.projectId = this.searchParam.projectId
      } else {
        delete query.projectId
      }

      if (
        this.searchParam.status &&
        this.searchParam.status !== virtualSportApprovalStatus.PENDING
      ) {
        query.status = this.searchParam.status
      } else {
        delete query.status
      }

      this.$router.push({ query }).catch(() => {})
    },

    getCurrentPageRange() {
      const currentPage = this.page
      const pageSize = 5
      const start = (currentPage - 1) * pageSize + 1
      const end = Math.min(currentPage * pageSize, this.total)
      return `${start} - ${end}`
    },
  },
}
</script>

<style lang="scss" scoped>
.data-list-container {
  padding: 28px;
  margin: 0 24px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;

  .empty-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 12px 0;
  }

  .empty-description {
    font-size: 15px;
    margin: 0;
    max-width: 420px;
    font-weight: 500;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-top: 20px;
}

@media (max-width: 1024px) {
  .data-list-container {
    margin: 0 20px;
    padding: 24px;
  }
}

@media (max-width: 768px) {
  .data-list-container {
    margin: 0 16px;
    padding: 20px;
    border-radius: 12px;
    min-height: 350px;
  }

  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;

    .results-info {
      .results-title {
        font-size: 16px;
      }

      .results-summary {
        font-size: 13px;
      }
    }

    .view-options {
      align-self: flex-end;
    }
  }

  .loading-container {
    padding: 60px 20px;

    .loading-spinner {
      width: 40px;
      height: 40px;
      border-width: 3px;
    }

    .loading-text {
      font-size: 14px;
    }
  }

  .empty-state {
    padding: 80px 20px;

    .empty-icon {
      font-size: 56px;
      margin-bottom: 20px;
    }

    .empty-title {
      font-size: 20px;
    }

    .empty-description {
      font-size: 14px;
    }
  }

  .items-list {
    margin-bottom: 24px;
  }
}

@media (max-width: 480px) {
  .data-list-container {
    margin: 0 12px;
    padding: 16px;
    min-height: 300px;
  }

  .loading-container {
    padding: 50px 16px;

    .loading-spinner {
      width: 36px;
      height: 36px;
    }

    .loading-text {
      font-size: 13px;
    }
  }

  .empty-state {
    padding: 60px 16px;

    .empty-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .empty-title {
      font-size: 18px;
    }

    .empty-description {
      font-size: 13px;
      line-height: 1.5;
    }
  }
}
</style>
